import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  DialogService,
  MessageType,
} from '../../../../app/features/shared/services/dialog.service';
import { getLogger, markAllAsTouched } from '../../../../app/core/utils';
import { NonNullableFormBuilder, Validators } from '@angular/forms';
import invariant from 'tiny-invariant';
import { StorageProvider } from '../../storage.provider';
import { StorageItem } from '@uploadcam/common';

export interface CreateFolderDialogInput {
  parent: StorageItem;
}

export type CreateFolderDialogResult = StorageItem;

@Component({
  selector: 'app-create-folder-dialog',
  templateUrl: './create-folder-dialog.component.html',
  styleUrls: ['./create-folder-dialog.component.scss'],
  standalone: false,
})
export class CreateFolderDialogComponent {
  public isCreating = false;
  public readonly form = this.fb.group({
    name: ['', [Validators.required]],
  });

  private readonly logger = getLogger('CreateFolderDialogComponent');

  constructor(
    @Inject(MAT_DIALOG_DATA) private readonly data: CreateFolderDialogInput,
    private readonly dialog: DialogService,
    private readonly dialogRef: MatDialogRef<CreateFolderDialogComponent, CreateFolderDialogResult>,
    private readonly fb: NonNullableFormBuilder,
    private readonly storageProvider: StorageProvider,
  ) {}

  public close(): void {
    this.dialogRef.close();
  }

  public async confirm(): Promise<void> {
    if (this.form.invalid) {
      return markAllAsTouched(this.form);
    }

    this.form.disable();
    this.isCreating = true;
    this.dialogRef.disableClose = true;

    this.logger.info(`Creating folder…`);

    try {
      invariant(this.form.value.name, 'Folder name must have a value');

      const manager = await this.storageProvider.provideManager();
      const folder = await manager.createFolder(this.data.parent, this.form.value.name);

      this.dialog.snack(`Folder successfully created`, MessageType.Success);
      this.dialogRef.close(folder);
    } catch (err) {
      this.logger.error(err);
      this.dialog.showErrorAlert(err, 'error.fallback.folder.create');
    } finally {
      this.form.enable();
      this.isCreating = false;
      this.dialogRef.disableClose = false;
    }
  }
}
